import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DIALOGUE_CLASS } from './dialogue';
import { humanize } from './schema';
import type { Values } from './types';

export function isDialogue(chain: string[]): boolean {
  return chain.includes(DIALOGUE_CLASS);
}

interface Page {
  bodyText?: string;
  animationCue?: string;
  choices?: { label?: string; targetPageIndex?: number }[];
}

/**
 * Plays a dialogue the way the game's window does (NPCDialogueSO): the NPC name on top, each page's
 * text typed out at typewriterSpeed characters per second (0 = at once), a click shows the rest, and
 * choices jump to their page (-1 closes). A page without choices continues to the next one.
 * Works on unsaved edits too, so writers can try a dialogue before saving it.
 */
export function DialoguePreview({ values, onClose }: { values: Values; onClose: () => void }) {
  const { t } = useTranslation();
  const pages = (Array.isArray(values.pages) ? values.pages : []) as Page[];
  const speed = typeof values.typewriterSpeed === 'number' ? values.typewriterSpeed : 40;
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(speed > 0 ? 0 : Infinity);
  const [closed, setClosed] = useState(pages.length === 0);
  const page = pages[index];
  const text = page?.bodyText ?? '';
  const done = shown >= text.length;

  useEffect(() => {
    if (closed || speed <= 0) return;
    const timer = setInterval(() => setShown((n) => (n >= text.length ? n : n + 1)), 1000 / speed);
    return () => clearInterval(timer);
  }, [index, closed, speed, text.length]);

  const go = (target: number) => {
    if (target < 0 || target >= pages.length) {
      setClosed(true);
      return;
    }
    setIndex(target);
    setShown(speed > 0 ? 0 : Infinity);
  };
  const restart = () => {
    setClosed(pages.length === 0);
    go(0);
  };
  const choices = page?.choices ?? [];

  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal dialogue-preview" role="dialog" aria-label={t('data.dialogue.preview')}>
        <header className="row">
          <strong>{String(values.npcName || '—')}</strong>
          {!closed && page?.animationCue && page.animationCue !== 'None' && (
            <span className="chip">{humanize(page.animationCue)}</span>
          )}
          <div className="spacer" />
          <span className="muted small">
            {closed
              ? t('data.dialogue.closed')
              : t('data.dialogue.pageOf', { n: index + 1, of: pages.length })}
          </span>
        </header>
        {closed ? (
          <div className="dialogue-text muted">{t('data.dialogue.closedHint')}</div>
        ) : (
          <div
            className="dialogue-text"
            onClick={() => setShown(Infinity)}
            title={done ? undefined : t('data.dialogue.skip')}
          >
            {renderLines(text.slice(0, shown))}
            {!done && <span className="caret">▌</span>}
          </div>
        )}
        <div className="dialogue-choices">
          {closed ? (
            <>
              <button onClick={restart}>{t('data.dialogue.restart')}</button>
              <button className="primary" onClick={onClose}>
                {t('common.close')}
              </button>
            </>
          ) : !done ? null : choices.length ? (
            choices.map((c, i) => (
              <button
                key={i}
                onClick={() => go(typeof c.targetPageIndex === 'number' ? c.targetPageIndex : -1)}
              >
                {c.label || '…'}
                <span className="muted small">
                  {' '}
                  →{' '}
                  {(c.targetPageIndex ?? -1) < 0
                    ? t('data.dialogue.close')
                    : t('data.dialogue.page', { n: (c.targetPageIndex ?? 0) + 1 })}
                </span>
              </button>
            ))
          ) : (
            <button className="primary" onClick={() => go(index + 1)}>
              {index + 1 < pages.length ? t('data.dialogue.continue') : t('data.dialogue.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Erik: text" lines get the speaker in bold, as writers mark who is talking. */
function renderLines(text: string) {
  return text.split('\n').map((line, i) => {
    const m = /^([A-ZÅÄÖ][\wÅÄÖåäö' -]{0,30}):(.*)$/.exec(line);
    return (
      <div key={i} className="dialogue-line">
        {m ? (
          <>
            <strong>{m[1]}:</strong>
            {m[2]}
          </>
        ) : (
          line || ' '
        )}
      </div>
    );
  });
}
